import { Dispatch, SetStateAction, useEffect } from 'react';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { UmsatzzielInputFields } from './UmsatzzielInputFields';
import { DialogHeader } from '../DialogHeader';
import { DialogFooterMessage } from '../../../components';
import { Button } from '@e2n/ui';
import { useTranslation } from 'react-i18next';
import { useFormSubmit } from '../useFormSubmit';
import { useNavigate } from 'react-router-dom';
import { useRouteParams } from '../useRouteParams';
import { AbsatzzielInputFields } from './AbsatzzielInputFields';
import { useDialogContext } from '../DialogContext';
import * as Sentry from '@sentry/react';
import { useStandortContext } from '@/features/standortContext/StandortContext';

// eslint-disable-next-line max-lines-per-function
export function EditForm({
  setHasChanges,
}: {
  setHasChanges: Dispatch<SetStateAction<boolean>>;
}) {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { umsatzplanungAnzeige } = useTagebuchEinstellungen(standortId);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchParams } = useRouteParams();
  const { dispatch, zieleForm } = useDialogContext();

  function handleClose() {
    dispatch({ type: 'reset' });
    navigate(
      `/controlling/umsatzplanung/${searchParams.jahr}/${searchParams.monat}`,
    );
  }

  function onSuccess() {
    dispatch({ type: 'reset' });
    navigate(
      `/controlling/umsatzplanung/${searchParams.jahr}/${searchParams.monat}?success=true&type=ziele`,
    );
  }

  function onError(error: Error) {
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t(
          'domain.controlling.umsatzplanung.ziele.errors.unknownError',
        ),
        type: 'error',
      },
    });
    Sentry.captureException(error);
  }

  const { handleSubmit } = useFormSubmit(onSuccess, onError);

  useEffect(() => {
    setHasChanges(zieleForm.hasChanges);
  }, [setHasChanges, zieleForm.hasChanges]);

  return (
    <>
      <div className="flex flex-col gap-6 pt-8 pb-4">
        <DialogHeader />
        <form className="flex flex-col gap-6">
          {umsatzplanungAnzeige !== 'ABSATZ_PRODUKTIVITAET' && (
            <UmsatzzielInputFields dispatch={dispatch} zieleForm={zieleForm} />
          )}
          {umsatzplanungAnzeige === 'UMSATZ_ABSATZ_PRODUKTIVITAET' && (
            <hr className="h-px bg-neutrals-300 border-0 col-span-2"></hr>
          )}
          {umsatzplanungAnzeige !== 'UMSATZ_PRODUKTIVITAET' && (
            <AbsatzzielInputFields dispatch={dispatch} zieleForm={zieleForm} />
          )}
        </form>
      </div>
      <hr className="h-px bg-neutrals-300 border-0 -mx-8"></hr>
      <div className="flex items-center justify-between pb-4">
        <DialogFooterMessage
          dialogFooterMessage={zieleForm.dialogMessage}
          isUpdating={zieleForm.isSubmitting}
        />
        <div className="flex items-center justify-end gap-4">
          <Button
            onClick={(e) => {
              if (zieleForm.hasChanges) {
                handleSubmit(e);
              } else {
                handleClose();
              }
            }}
            disabled={zieleForm.isSubmitting}>
            {t('domain.controlling.umsatzplanung.ziele.fertig')}
          </Button>
        </div>
      </div>
    </>
  );
}
